/**
 * NACHTRAG-VERSION, EINHEIT 5 (30.10.2026): ZWEI WEGE, EINE ENTSCHEIDUNG DES SERVERS.
 *
 * - Vorgang MIT gültiger unterschriebener Kostenübernahme (KÜ V2, `gilt` in `/cost-acceptances`, serverseitig
 *   `gueltige_kostenuebernahme`, z. B. V-1011): die unterschriebene KÜ ist der Vertrag, ein Nachtrag per Klick-Zustimmung
 *   ist dort gesperrt (409 `gueltige_kue`). Es bleibt der alte Weg „Für Änderung öffnen“ -> Änderung -> neue KÜ zur Unterschrift.
 * - Alle anderen: „Nachtrag erstellen“ (Link in den Builder), „Für Änderung öffnen“ antwortet dort mit 410.
 *
 * Der Knopf „Für Änderung öffnen“ unten ist der unveränderte Code von origin/main (`FuerAenderungOeffnenKue`).
 */
/**
 * „FÜR ÄNDERUNG ÖFFNEN" — der Knopf zu einer Route, die es längst gibt (WARTESCHLANGE Punkt 29, P1).
 *
 * Antoine am 16.09.2026, mit Bildschirmfoto: **„Der Kunde möchte Änderungen, diese müssen möglich
 * sein."** Sobald ein Gast zugesagt hat, ist das Angebot gesperrt — jede Position, jeder Preis,
 * jede Eckdate. Das ist richtig so: an einer angenommenen Fassung darf sich nichts unbemerkt
 * ändern. Falsch war nur, dass es **keinen Weg zurück** gab.
 *
 * Gebaut ist der Weg seit dem 17.09.2026 (`POST /api/events/:id/amendment/reopen`): Der Vorgang
 * geht von `offer_chosen`/`payment_failed` zurück auf `offer_sent`, die zugestimmte Fassung wird
 * geleert, und der Schritt steht mit Begründung im Verlauf. **Was fehlte, war der Knopf.**
 *
 * ── DREI DINGE, DIE DIESER DIALOG BEWUSST TUT ───────────────────────────────────────────────
 *
 * 1. **Die Begründung ist Pflicht, und zwar sichtbar.** Der Server verlangt sie ohnehin; ihn
 *    ablehnen zu lassen wäre eine Fehlermeldung statt einer Führung. Sie landet im Verlauf und
 *    beantwortet später die Frage, warum eine Zusage nicht mehr gilt.
 * 2. **Der Dialog sagt die FOLGE, nicht die Handlung.** „Der Kunde muss danach erneut zustimmen"
 *    ist das, was der Betreiber wissen muss — „setzt den Status auf offer_sent" ist es nicht.
 * 3. **Kein Geld wird angefasst.** Bestehende Zahlungen, Links und Rechnungen bleiben, wie sie
 *    sind. Das ist keine Auslassung, sondern die Grenze dieser Ausbaustufe (Loop-Dokument P3
 *    hängt an Antoines ausdrücklicher Freigabe). Deshalb steht es auch im Dialog: Wer mehr
 *    erwartet, soll es vorher lesen und nicht hinterher suchen.
 */
import { useState } from "react";
import { apiMutate } from "../../hooks/useApi";
import { Button, Drawer, ErrorNote, Icon, Select } from "../components";
import { AenderungStatuskarte } from "./AenderungStatuskarte";
import { ANLASS_TEXT, gueltigeText, useKueVersionen, versionsName, type KueVersion } from "./KostenuebernahmeVersionen";

/**
 * Die Zustände, aus denen die Route heraus öffnet (`routes/events.ts`), seit KÜ V2 Stufe S3 (E9)
 * auch „bezahlt“. Eine abgeschlossene Abrechnung sperrt serverseitig (409 mit Klartext).
 */
export const WIEDEROEFFENBAR: readonly string[] = ["offer_chosen", "payment_failed", "balance_due", "paid"];

type Anlass = NonNullable<KueVersion["aenderungsAnlass"]>;
const ANLASS_OPTIONEN = (Object.keys(ANLASS_TEXT) as Anlass[]).map((value) => ({ value, label: ANLASS_TEXT[value] }));


function FuerAenderungOeffnenKue({
  eventId,
  status,
  onGeoeffnet,
  knopf = false,
}: {
  /** Als ausgefüllter Knopf statt als kleiner Textlink (Builder-Hinweis, Senden-Dialog). */
  knopf?: boolean;
  eventId: string;
  status: string;
  /** Der Vorgang hat einen neuen Status — die Seite muss neu laden. */
  onGeoeffnet: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState("");
  const [anlass, setAnlass] = useState<Anlass | "">("");
  // Nur geladen, wenn der Dialog offen ist: welche Kostenübernahme gilt bis zur neuen Unterschrift.
  const gueltige = useKueVersionen(offen ? eventId : null).data?.find((v) => v.gilt) ?? null;
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (!WIEDEROEFFENBAR.includes(status)) return null;

  async function oeffne() {
    const sauber = grund.trim();
    if (sauber.length === 0 || !anlass) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/amendment/reopen`, "POST", { reason: sauber, anlass });
      setOffen(false);
      setGrund("");
      setAnlass("");
      onGeoeffnet();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {knopf ? (
        <Button type="button" data-testid="fuer-aenderung-oeffnen" onClick={() => setOffen(true)} className="!py-2">
          <Icon name="lock_open" className="text-[16px]" /> Für Änderung öffnen
        </Button>
      ) : (
        <button
          type="button"
          data-testid="fuer-aenderung-oeffnen"
          onClick={() => setOffen(true)}
          className="inline-flex items-center gap-1.5 font-label-md text-[12px] text-primary hover:underline"
        >
          <Icon name="lock_open" className="text-[15px]" /> Für Änderung öffnen
        </button>
      )}

      <Drawer open={offen} onClose={() => setOffen(false)} title="Angebot für Änderungen öffnen">
        <div className="space-y-4">
          {gueltige ? (
            <p className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface" data-testid="oeffnen-gueltige-kue">
              Das Angebot wird wieder änderbar. <strong>Die unterschriebene {gueltigeText(gueltige)} bleibt gültig</strong>,
              bis der Gast eine neue Kostenübernahme unterschreibt. An den Gast geht jetzt keine Mail.
            </p>
          ) : (
            <p className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface">
              Das Angebot wird wieder änderbar. <strong>Der Kunde muss danach erneut zustimmen</strong> —
              seine bisherige Zusage gilt dann nicht mehr.
            </p>
          )}
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {gueltige
              ? "Bestehende Zahlungen, Zahlungslinks und Rechnungen bleiben bis zur Unterschrift unverändert. Mit der Unterschrift unter die neue Kostenübernahme werden die offenen Zahlungslinks auf den neuen Betrag umgestellt; bereits Bezahltes wird angerechnet."
              : "Bestehende Zahlungen, Zahlungslinks und Rechnungen bleiben unverändert. Ändert sich der Betrag, passen Sie sie anschließend selbst an."}
          </p>

          {fehler && <ErrorNote message={fehler} />}

          <div>
            <span className="mb-1 block font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">
              Anlass <span className="text-error">*</span>
            </span>
            <Select
              value={anlass}
              onValueChange={(v) => setAnlass(v as Anlass)}
              options={ANLASS_OPTIONEN}
              placeholder="Bitte wählen"
              ariaLabel="Anlass der Änderung"
              disabled={busy}
              testId="oeffnen-anlass"
            />
            {anlass === "betriebsfehler" && gueltige && (
              <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" data-testid="oeffnen-hinweis-betriebsfehler">
                Auch bei einem eigenen Fehler bleibt {versionsName(gueltige)} bindend, bis der Gast der neuen Fassung zustimmt.
              </p>
            )}
          </div>

          <label className="block">
            <span className="mb-1 block font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">
              Warum wird geöffnet? <span className="text-error">*</span>
            </span>
            <textarea
              value={grund}
              rows={3}
              disabled={busy}
              autoFocus
              placeholder="z. B. Kundin möchte 10 Gäste mehr und ein vegetarisches Hauptgericht"
              onChange={(e) => setGrund(e.target.value)}
              className="w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 font-body-md text-body-md text-on-background disabled:opacity-60 dark:border-outline/40 dark:bg-surface-variant/10 dark:text-inverse-on-surface"
            />
            <span className="mt-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Steht später im Verlauf des Vorgangs und beantwortet die Frage, warum wieder verhandelt
              wurde.
            </span>
          </label>

          <div className="flex items-center gap-3 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
            <Button type="button" className="!py-2" disabled={busy || grund.trim().length === 0 || !anlass} onClick={() => void oeffne()}>
              {busy ? "Öffnet …" : "Für Änderung öffnen"}
            </Button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setOffen(false)}
              className="font-label-md text-label-md text-secondary hover:underline disabled:opacity-50 dark:text-secondary-fixed-dim"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
}

/** Gilt für den Vorgang eine unterschriebene Kostenübernahme? (Server entscheidet: `gilt` aus `gueltige_kostenuebernahme`.) */
export function useHatGueltigeKue(eventId: string | null): { geladen: boolean; gueltig: boolean } {
  const q = useKueVersionen(eventId);
  return { geladen: !q.loading && q.data != null, gueltig: !!q.data?.some((v) => v.gilt) };
}

export function FuerAenderungOeffnen({
  eventId,
  status,
  onGeoeffnet,
  knopf = false,
}: {
  eventId: string;
  status: string;
  /** Der Vorgang hat einen neuen Status (nur beim KÜ-Weg) — die Seite muss neu laden. */
  onGeoeffnet?: () => void;
  /** Als ausgefüllter Knopf statt als Textlink. */
  knopf?: boolean;
}) {
  const { gueltig } = useHatGueltigeKue(WIEDEROEFFENBAR.includes(status) ? eventId : null);
  if (!WIEDEROEFFENBAR.includes(status)) return null;
  if (gueltig) return <FuerAenderungOeffnenKue eventId={eventId} status={status} knopf={knopf} onGeoeffnet={onGeoeffnet ?? (() => window.location.reload())} />;
  return (
    <a
      href={`/vorgaenge/${eventId}/angebot`}
      data-testid="nachtrag-erstellen-link"
      className="inline-flex items-center gap-1.5 font-label-md text-[12px] text-primary hover:underline"
    >
      <Icon name="edit_note" className="text-[15px]" /> Nachtrag erstellen
    </a>
  );
}

/**
 * DAS BANNER WÄHREND DER ÄNDERUNG — „das Angebot steht offen, der Kunde hat noch nicht zugestimmt".
 *
 * Nach dem Wiederöffnen sieht der Vorgang aus wie jeder andere versendete: Status `offer_sent`,
 * keine zugestimmte Fassung. **Genau das ist die Gefahr.** Der Betreiber könnte glauben, die
 * Zusage von gestern gelte noch — sie gilt nicht, und niemand sagt es ihm.
 *
 * KEINE NEUE SPALTE, sondern aus dem Verlauf abgeleitet, und die Regel ist bewusst eng:
 * **die jüngste Zeile „wieder geöffnet" muss jünger sein als die jüngste Zeile „zugesagt".** Nur
 * dann steht die Änderung wirklich noch aus. Hätte man bloß auf „es gibt eine Reopen-Zeile"
 * geprüft, bliebe das Banner nach der erneuten Zusage für immer stehen — ein Hinweis, der nicht
 * mehr stimmt, ist schlimmer als keiner.
 */
export function AenderungOffenBanner({
  status,
  aktivitaeten = [],
  eventId,
  onVerworfen,
  kundenName,
}: {
  status: string;
  aktivitaeten?: { action: string; createdAt: string }[];
  /** KÜ V2 (S1): mit Vorgangs-Id zeigt das Banner, welche Kostenübernahme bis zur neuen Unterschrift gilt. */
  eventId?: string;
  /** KÜ V2 (S2): nach „Änderung verwerfen“ — die Seite lädt neu (ohne: ganze Seite neu laden). */
  onVerworfen?: () => void;
  /** Kunde bzw. Firma für den Satz „Bis … unterschreibt“ (fehlt: „der Gast“). */
  kundenName?: string | null;
}) {
  const versionenQ = useKueVersionen(status === "offer_sent" && eventId ? eventId : null);
  const gueltige = versionenQ.data?.find((v) => v.gilt) ?? null;
  if (status !== "offer_sent") return null;
  const juengste = (art: string): number => {
    let max = 0;
    for (const a of aktivitaeten) {
      if (a.action !== art) continue;
      const t = new Date(a.createdAt).getTime();
      if (Number.isFinite(t) && t > max) max = t;
    }
    return max;
  };
  const geoeffnet = juengste("offer.reopened_for_amendment");

  /*
   * KÜ V2: eine gültige, unterschriebene Kostenübernahme bei `offer_sent` heißt immer „in Änderung“
   * — die Unterschrift hat den Vorgang auf `offer_chosen` gehoben, zurück kommt er nur durch
   * „Für Änderung öffnen“. Das Banner braucht dafür keinen Verlauf (der Builder hat keinen).
   */
  if (gueltige) {
    /*
     * Amendment P4 (KONZEPT § 8): reiner Hinweis, kein automatischer Auslöser. Er verschwindet,
     * sobald eine neuere, nicht verworfene Fassung angefordert ist — dann ist er erledigt.
     */
    const neue = (versionenQ.data ?? []).find(
      (v) => !v.isTest && (v.versionNo ?? 0) > (gueltige.versionNo ?? 0) && v.status !== "withdrawn" && v.status !== "declined" && v.status !== "signed_ungueltig",
    );
    if (!eventId) return null;
    const gesendet = juengste("offer.sent");
    return (
      <AenderungStatuskarte
        eventId={eventId}
        gueltige={gueltige}
        neueKue={neue ?? null}
        kundenName={kundenName}
        angebotGesendetAm={geoeffnet > 0 && gesendet > geoeffnet ? new Date(gesendet).toISOString() : null}
        versionen={versionenQ.data ?? []}
        onVerworfen={onVerworfen}
      />
    );
  }
  if (geoeffnet === 0 || geoeffnet <= juengste("offer.chosen")) return null;

  return (
    <p
      className="mb-3 flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-[11.5px] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
      data-testid="aenderung-offen-banner"
    >
      <Icon name="lock_open" className="mt-0.5 text-[14px]" />
      <span>
        <b>Angebot ist für Änderungen geöffnet</b> — der Kunde muss erneut zustimmen. Die frühere
        Zusage gilt nicht mehr.
      </span>
    </p>
  );
}

